import { BankAccountType, useWealthOverview } from '@egw/api'
import { GenericError } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { type FC, useMemo } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { Navigate } from 'react-router'

export const AccountActivatePage: FC = () => {
  const { t } = useTranslation('features-account')

  const { data: wealthOverviewData, isLoading, isError } = useWealthOverview()

  // Find the first checking account from the wealth overview data
  const checkingAccount = useMemo(() => {
    if (!wealthOverviewData?.highYieldChecking?.accounts) {
      return null
    }

    return wealthOverviewData.highYieldChecking.accounts.find(
      (account) => account.accountType === BankAccountType.HIGH_YIELD_CHECKING,
    )
  }, [wealthOverviewData])

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="egw-container">
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={1} height="100px" className="mb-10" />
          <Skeleton count={5} height="50px" />
        </SkeletonTheme>
      </div>
    )
  }

  // Show error state if there's an error
  if (isError) {
    return (
      <div className="egw-container">
        <GenericError errorMessage={t('common.generic_error')} />
      </div>
    )
  }

  // If no checking account is found, redirect to overview
  if (!checkingAccount) {
    return <Navigate to={WEALTH_ROUTES.OVERVIEW.path} />
  }

  // Redirect to the debit card activation route with the checking account alias
  return (
    <Navigate
      to={WEALTH_ROUTES.CHECKING_ACCOUNT_DEBIT_CARD_ACTIVATE.path.replace(':alias', checkingAccount.alias.toString())}
      replace
    />
  )
}
